import { Injectable } from '@angular/core';
import { AngularFirestore, QueryFn } from '@angular/fire/compat/firestore';
import { firstValueFrom } from 'rxjs';

import UniqueEntityID from '../../../core/domain/unique_entity_id';
import {
  DiaryType,
  DiaryTypeProps,
} from '../../tracker/domain/diary_type/diary_type';
import { DiaryTypeSchema } from '../../tracker/repositories/diary_type_repository';
import {
  FavoriteDiary,
  FavoriteDiaryDescriptionProps,
} from '../domain/favorite-diary/favorite-diary';

interface FavoriteDiaryDescriptionSchema {
  title: string;
  content: string;
}

export interface FavoriteDiarySchema {
  descriptions: FavoriteDiaryDescriptionSchema[];
  diaryType: DiaryTypeSchema;
}

@Injectable()
export class FavoriteDiaryRepository {
  constructor(private firestore: AngularFirestore) {
    // do nothing
  }

  private collection(patientId: string, queryFn?: QueryFn) {
    return this.firestore
      .collection('patients')
      .doc(patientId)
      .collection('favorite_diaries', queryFn);
  }

  private fromSchema(schema: FavoriteDiarySchema, id?: string) {
    return FavoriteDiary.create(
      {
        diaryType: DiaryType.create({
          name: schema.diaryType.name,
          displayed: schema.diaryType.displayed,
          hint: schema.diaryType.hint,
          icon: schema.diaryType.icon,
          label: schema.diaryType.label,
          sorter: schema.diaryType.sorter,
        } as DiaryTypeProps),
        descriptions: schema.descriptions.map(
          (d) =>
            <FavoriteDiaryDescriptionProps>{
              title: d.title,
              content: d.content,
            },
        ),
      },
      new UniqueEntityID(id),
    );
  }

  private toSchema(favorite: FavoriteDiary) {
    return <FavoriteDiarySchema>{
      diaryType: <DiaryTypeSchema>{
        name: favorite.diaryType.name,
        icon: favorite.diaryType.icon,
        hint: favorite.diaryType.hint,
        label: favorite.diaryType.label,
        displayed: favorite.diaryType.displayed,
        sorter: favorite.diaryType.sorter,
      },
      descriptions: favorite.descriptions.map((d) => {
        return <FavoriteDiaryDescriptionSchema>{
          title: d.title,
          content: d.content,
        };
      }),
    };
  }

  async findAll(patientId: string) {
    const snap = await firstValueFrom(this.collection(patientId).get());

    return snap.docs.map((doc) =>
      this.fromSchema(doc.data() as FavoriteDiarySchema, doc.id),
    );
  }

  async findAllByDiaryType(patientId: string, diaryType: DiaryType) {
    const snap = await firstValueFrom(
      this.collection(patientId, (ref) =>
        ref.where('diaryType.name', '==', diaryType.name),
      ).get(),
    );

    return snap.docs.map((doc) =>
      this.fromSchema(doc.data() as FavoriteDiarySchema, doc.id),
    );
  }

  async save(
    description: FavoriteDiaryDescriptionProps,
    diaryType: DiaryType,
    patientId: string,
  ) {
    const favorites = await this.findAllByDiaryType(patientId, diaryType);
    if (favorites.length > 0) {
      const schema = this.toSchema(favorites[0]);
      const id = favorites[0].id.toString();
      schema.descriptions.push(description);
      await this.collection(patientId).doc(id).set(schema);
      return this.fromSchema(schema, id);
    } else {
      const schema: FavoriteDiarySchema = {
        diaryType: {
          name: diaryType.name,
          label: diaryType.label,
          displayed: diaryType.displayed,
          hint: diaryType.hint,
          icon: diaryType.icon,
          sorter: diaryType.sorter,
        },
        descriptions: [
          { title: description.title, content: description.content },
        ],
      };
      const ref = await this.collection(patientId).add(schema);
      return this.fromSchema(schema, ref.id);
    }
  }

  async update(favoriteDiary: FavoriteDiary, patientId: string) {
    const schema = this.toSchema(favoriteDiary);
    await this.collection(patientId)
      .doc(favoriteDiary.id.toString())
      .set(schema);
    return this.fromSchema(schema, favoriteDiary.id.toString());
  }
}
